import { useCrmLocale } from '../ui/locale/crmLocale';
import React from 'react';
import { Link, type LinkProps } from 'react-router-dom';

// Detect external URLs (protocol or protocol-relative)
// Security: Simplified regex to avoid ReDoS vulnerability
const isExternal = (href: string) => {
  if (!href) return false;
  // Match http://, https://, //, or other protocols
  return href.startsWith('//') || /^[a-z][a-z0-9+.-]{0,20}:/i.test(href);
};

const sanitizeHref = (href: string) => {
  const value = (href || '').trim();
  if (!value) return '#';
  if (value.startsWith('//')) return '#';
  const match = value.match(/^([a-z][a-z0-9+.-]{0,20}):/i);
  if (match) {
    const scheme = match[1].toLowerCase();
    if (scheme !== 'http' && scheme !== 'https' && scheme !== 'mailto' && scheme !== 'tel') {
      return '#';
    }
  }
  return value;
};

export interface SmartLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  external?: boolean;
  showExternalIcon?: boolean;
  newTab?: boolean;
  className?: string;
  internalOverride?: boolean; // force treat as internal even if regex says external
}

/** SmartLink chooses <Link> for internal navigation and <a> for external links. */
export const SmartLink: React.FC<SmartLinkProps> = ({
  to,
  external,
  showExternalIcon,
  newTab,
  className,
  internalOverride,
  children,
  ...rest
}) => {
  const { t: crmText } = useCrmLocale();

  const safeTo = sanitizeHref(to);
  const treatAsExternal = external ?? (!internalOverride && isExternal(safeTo));
  if (treatAsExternal) {
    // Security hardening: ensure rel contains noopener noreferrer for any external link
    let relBase = rest.rel || '';
    const needed = ['noopener','noreferrer'];
    needed.forEach(tok=>{ if(!relBase.toLowerCase().includes(tok)) relBase = (relBase? relBase + ' ' : '') + tok; });
    const rel = newTab ? relBase : relBase; // newTab handled by target, rel already includes safe tokens
    const target = newTab ? '_blank' : rest.target;
    return (
      <a href={safeTo} className={className} target={target} rel={rel} {...rest}>
        {children}
        {(showExternalIcon || newTab) && (
          <span aria-hidden="true" className="ml-1 inline-block align-baseline opacity-70">↗</span>
        )}
        {(showExternalIcon || newTab) && (
          <span className="sr-only">{crmText("(opens in a new tab)")}</span>
        )}
      </a>
    );
  }
  const internalProps: LinkProps = { to: safeTo } as LinkProps;
  return (
    <Link {...internalProps} className={className} {...rest}>
      {children}
    </Link>
  );
};

export default SmartLink;
